import { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';

/**
 * Extract the `exp` claim from a token.
 * Supports:
 *   1. JSON bundle objects (top-level `exp`)
 *   2. Individual proof JWTs inside `jwts` (min `exp` as fallback)
 *   3. Raw JWT strings
 */
function getExpFromToken(token) {
  if (!token) return null;

  // Try as JSON bundle (the format used by verificationJWT in this codebase)
  try {
    const data = typeof token === 'string' ? JSON.parse(token) : token;

    // 1. Top-level exp
    if (data?.exp) return data.exp;

    // 2. Fallback: min exp across individual proof JWTs in the bundle
    if (data?.jwts && typeof data.jwts === 'object') {
      let minExp = null;
      for (const proof of Object.values(data.jwts)) {
        const proofExp = proof?.exp;
        if (proofExp && (minExp === null || proofExp < minExp)) {
          minExp = proofExp;
        }
      }
      if (minExp) return minExp;
    }
  } catch {
    // Not a JSON bundle — fall through
  }

  // 3. Try as a raw JWT string
  if (typeof token === 'string') {
    try {
      const { exp } = jwtDecode(token);
      return exp || null;
    } catch {
      return null;
    }
  }

  return null;
}

function formatRemaining(ms) {
  if (ms <= 0) return 'Expired';

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes >= 5) return `${minutes}m`;
  if (minutes >= 1) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Read the `expiresAt` timestamp from a useLocalStorage entry.
 * Returns epoch-seconds (like JWT exp) or null.
 */
function getExpFromLocalStorage(storageKey) {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const { expiresAt } = JSON.parse(raw);
    if (expiresAt) return expiresAt / 1000; // convert ms → seconds
  } catch (err) {
    console.warn('useTokenExpiry: failed to read localStorage expiry for key:', storageKey, err);
  }
  return null;
}

export function useTokenExpiry(token, storageKey) {
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    const exp = getExpFromToken(token) || getExpFromLocalStorage(storageKey);
    if (!exp) {
      setTimeRemaining(null);
      return;
    }

    const update = () => setTimeRemaining((exp * 1000) - Date.now());

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [token, storageKey]);

  const isExpired = timeRemaining !== null && timeRemaining <= 0;
  const formattedTime = timeRemaining !== null ? formatRemaining(timeRemaining) : null;

  return { timeRemaining, isExpired, formattedTime };
}
